import type {
  CompleteUploadRequest,
  CreateFolderRequest,
  CreateShareRequest,
  CreateUploadSessionRequest,
  DownloadUrlResponse,
  FileObjectResponse,
  FileShareResponse,
  FolderDetailResponse,
  MoveFileRequest,
  PaginatedResponse,
  UpdateFolderRequest,
  UploadSessionResponse,
} from '@tabita/contracts';
import { apiRequest } from './api-client';

export interface ListFilesParams {
  readonly ownerType?: string | undefined;
  readonly ownerId?: string | undefined;
  /** Absent means the drive's root; an id means that folder's contents. */
  readonly folderId?: string | undefined;
  readonly includeTrashed?: boolean | undefined;
  readonly limit?: number | undefined;
  readonly cursor?: string | undefined;
}

/**
 * Files, folders and share links — "Mi Nube" — plus the plain
 * upload/attach flow other modules use for a photo or a logo (`requestUpload`
 * → `putBytes` → `completeUpload`, no folder, no owner beyond the entity
 * being attached to). See `publicCloudShareApi` for the anonymous half of
 * opening a share link, with no session at all.
 */
export const filesApi = {
  list(params: ListFilesParams = {}, signal?: AbortSignal) {
    return apiRequest<PaginatedResponse<FileObjectResponse>>('/files', {
      query: { ...params },
      signal,
    });
  },

  getFile(fileId: string, signal?: AbortSignal) {
    return apiRequest<FileObjectResponse>(`/files/${fileId}`, { signal });
  },

  requestUpload(body: CreateUploadSessionRequest) {
    return apiRequest<UploadSessionResponse>('/files/upload-sessions', { method: 'POST', body });
  },

  completeUpload(sessionId: string, body: CompleteUploadRequest) {
    return apiRequest<FileObjectResponse>(`/files/upload-sessions/${sessionId}/complete`, {
      method: 'POST',
      body,
    });
  },

  getDownloadUrl(fileId: string) {
    return apiRequest<DownloadUrlResponse>(`/files/${fileId}/download-url`);
  },

  moveFile(fileId: string, body: MoveFileRequest) {
    return apiRequest<FileObjectResponse>(`/files/${fileId}/move`, { method: 'POST', body });
  },

  /** Trash — the first of the two steps a permanent delete requires. */
  archive(fileId: string) {
    return apiRequest<FileObjectResponse>(`/files/${fileId}/archive`, { method: 'POST' });
  },

  restoreFile(fileId: string) {
    return apiRequest<FileObjectResponse>(`/files/${fileId}/restore`, { method: 'POST' });
  },

  deleteFileForever(fileId: string) {
    return apiRequest<void>(`/files/${fileId}`, { method: 'DELETE' });
  },

  /**
   * Uploads bytes straight to the signed URL — never through `apiRequest`,
   * which always sends a bearer token and JSON-encodes its body. The
   * signature in the URL is the only authorization this call carries, the
   * same way a real S3 presigned URL works.
   */
  async putBytes(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    });
    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`);
    }
  },

  // --- Folders ("Mi Nube") ---------------------------------------------------

  listFolders(
    params: { parentFolderId?: string; includeTrashed?: boolean } = {},
    signal?: AbortSignal,
  ) {
    return apiRequest<FolderDetailResponse[]>('/files/folders', { query: { ...params }, signal });
  },
  getFolder(id: string, signal?: AbortSignal) {
    return apiRequest<FolderDetailResponse>(`/files/folders/${id}`, { signal });
  },
  createFolder(body: CreateFolderRequest) {
    return apiRequest<FolderDetailResponse>('/files/folders', { method: 'POST', body });
  },
  renameFolder(id: string, body: UpdateFolderRequest) {
    return apiRequest<FolderDetailResponse>(`/files/folders/${id}`, { method: 'PATCH', body });
  },
  trashFolder(id: string) {
    return apiRequest<FolderDetailResponse>(`/files/folders/${id}/trash`, { method: 'POST' });
  },
  restoreFolder(id: string) {
    return apiRequest<FolderDetailResponse>(`/files/folders/${id}/restore`, { method: 'POST' });
  },
  deleteFolderForever(id: string) {
    return apiRequest<void>(`/files/folders/${id}`, { method: 'DELETE' });
  },

  // --- Share links ---------------------------------------------------------------

  createShare(fileId: string, body: CreateShareRequest) {
    return apiRequest<FileShareResponse>(`/files/${fileId}/shares`, { method: 'POST', body });
  },
  listShares(fileId: string, signal?: AbortSignal) {
    return apiRequest<FileShareResponse[]>(`/files/${fileId}/shares`, { signal });
  },
  revokeShare(id: string) {
    return apiRequest<void>(`/files/shares/${id}/revoke`, { method: 'POST' });
  },
  /** Opens a `SPECIFIC_PEOPLE` share as the signed-in recipient — see
   *  `FileShareController.unlockAsViewer`'s own comment for why this is on
   *  the authenticated surface rather than `publicCloudShareApi`. */
  unlockShareAsViewer(token: string, password?: string) {
    return apiRequest<DownloadUrlResponse>(`/files/shares/${token}/unlock-as-viewer`, {
      method: 'POST',
      body: { password: password ?? null },
    });
  },
};
